import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { getMyDiaryEntries } from '../../../api';

const PAGE_SIZE = 20;

// Columns are at least this wide; as many as fit the space are used.
const MIN_COLUMN_WIDTH = 280;
const COLUMN_GAP = 16; // gap-4

// Rough card heights (px) before the card is drawn, to pick the shortest column.
const estimateHeight = (entry) => {
  const lines = (text, perLine) => Math.ceil(String(text || '').length / perLine);
  let height = 110; // badge, date and title
  if (entry.type === 'text') height += lines(entry.content, 38) * 24;
  else if (entry.type === 'audio') height += 60;
  else height += entry.mediaUrl ? 260 : 24;
  if (entry.note) height += 30 + lines(entry.note, 40) * 20;
  return height;
};

// Splits the entries into `count` columns: each goes under the shortest one,
// so a short card never leaves a gap beside a tall one. The first `count`
// entries start the columns left to right, keeping newest-first at the top.
const toColumns = (entries, count) => {
  const columns = Array.from({ length: count }, () => ({ height: 0, entries: [] }));
  entries.forEach((entry) => {
    const shortest = columns.reduce((best, column) => (column.height < best.height ? column : best));
    shortest.entries.push(entry);
    shortest.height += estimateHeight(entry) + COLUMN_GAP;
  });
  return columns.map((column) => column.entries);
};

// How many columns fit the list's width (one until it can be measured).
function useColumnCount(element) {
  const [count, setCount] = useState(1);

  useEffect(() => {
    if (!element || typeof ResizeObserver !== 'function') return undefined;
    const measure = () =>
      setCount(Math.max(1, Math.floor((element.clientWidth + COLUMN_GAP) / (MIN_COLUMN_WIDTH + COLUMN_GAP))));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);

  return count;
}

const FALLBACK_TAB = { label: 'Creation', icon: '📄', bg: 'bg-gray-100', text: 'text-gray-700' };

const formatDate = (value) =>
  new Date(value).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

function StatusCard({ icon, title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 flex flex-col items-center text-center gap-2">
      <span className="text-5xl">{icon}</span>
      <p className="text-lg font-extrabold text-slate-800">{title}</p>
      {children}
    </div>
  );
}

function EntryBody({ entry }) {
  if (entry.type === 'text') {
    return <p className="text-[15px] text-slate-700 whitespace-pre-wrap break-words">{entry.content}</p>;
  }

  if (!entry.mediaUrl) {
    return <p className="text-sm font-medium text-gray-400">This {entry.type} isn&apos;t available right now.</p>;
  }

  if (entry.type === 'video') {
    return (
      <video
        src={entry.mediaUrl}
        aria-label={`${entry.title} (video)`}
        controls
        preload="metadata"
        className="w-full max-h-[320px] rounded-xl bg-black"
      />
    );
  }
  if (entry.type === 'audio') {
    return <audio src={entry.mediaUrl} aria-label={`${entry.title} (audio)`} controls preload="metadata" className="w-full" />;
  }
  return <img src={entry.mediaUrl} alt={entry.title} loading="lazy" className="w-full max-h-[320px] rounded-xl object-cover" />;
}

function CreationCard({ entry, tab }) {
  return (
    <article className="min-w-0 bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 text-xs font-extrabold ${tab.bg} ${tab.text}`}>
          <span>{tab.icon}</span>
          {tab.label}
        </span>
        <time dateTime={entry.createdAt} className="ml-auto whitespace-nowrap text-xs font-semibold text-gray-400">
          {formatDate(entry.createdAt)}
        </time>
      </div>

      <h3 className="text-lg font-extrabold text-slate-900 break-words">{entry.title}</h3>

      <EntryBody entry={entry} />

      {entry.note && (
        <p className="rounded-xl bg-gray-50 px-4 py-3 text-sm font-medium text-slate-600 whitespace-pre-wrap break-words">
          📝 {entry.note}
        </p>
      )}
    </article>
  );
}

/**
 * "My Creations": the student's own published diary entries, newest first.
 * Fetches when it mounts (so it's fresh every time the tab is opened) and
 * pages in more with "Load more". `tabs` is DiaryZone's TABS list, reused for
 * each entry's type label / colours.
 */
export default function DiaryCreations({ tabs }) {
  const [entries, setEntries] = useState([]);
  const [page, setPage] = useState(0); // last page loaded
  const [totalPages, setTotalPages] = useState(0);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [listElement, setListElement] = useState(null);
  const columnCount = useColumnCount(listElement);

  const loadPage = useCallback(async (pageNumber) => {
    try {
      const result = await getMyDiaryEntries({ page: pageNumber, limit: PAGE_SIZE });
      const fetched = result?.data?.entries || [];
      setEntries((previous) => (pageNumber === 1 ? fetched : [...previous, ...fetched]));
      setPage(pageNumber);
      setTotalPages(result?.data?.pagination?.totalPages || 0);
      setStatus('ready');
    } catch (error) {
      if (pageNumber === 1) {
        setStatus('error');
      } else {
        toast.error("Couldn't load more creations. Please try again!");
      }
    }
  }, []);

  useEffect(() => {
    loadPage(1);
  }, [loadPage]);

  const handleRetry = () => {
    setStatus('loading');
    loadPage(1);
  };

  const handleLoadMore = async () => {
    setIsLoadingMore(true);
    await loadPage(page + 1);
    setIsLoadingMore(false);
  };

  if (status === 'loading') {
    return <StatusCard icon="⏳" title="Loading your creations..." />;
  }

  if (status === 'error') {
    return (
      <StatusCard icon="😕" title="We couldn't load your creations">
        <p className="text-sm font-medium text-gray-500">Please check your connection and try again.</p>
        <button
          onClick={handleRetry}
          className="mt-2 rounded-2xl bg-indigo-600 px-6 py-3 font-extrabold text-white hover:bg-indigo-700 transition-colors"
        >
          Try again
        </button>
      </StatusCard>
    );
  }

  if (entries.length === 0) {
    return (
      <StatusCard icon="🗂️" title="No creations yet">
        <p className="text-sm font-medium text-gray-500">Anything you publish from Create New will show up here.</p>
      </StatusCard>
    );
  }

  const tabsById = Object.fromEntries(tabs.map((tab) => [tab.id, tab]));

  return (
    <div className="flex flex-col gap-4">
      <div ref={setListElement} className="flex gap-4 items-start">
        {toColumns(entries, columnCount).map((column, index) => (
          <div key={index} className="flex-1 min-w-0 flex flex-col gap-4">
            {column.map((entry) => (
              <CreationCard key={entry._id} entry={entry} tab={tabsById[entry.type] || FALLBACK_TAB} />
            ))}
          </div>
        ))}
      </div>

      {page < totalPages && (
        <button
          onClick={handleLoadMore}
          disabled={isLoadingMore}
          className="self-center rounded-2xl bg-indigo-50 px-6 py-3 font-extrabold text-indigo-600 hover:bg-indigo-100 disabled:opacity-60 transition-colors"
        >
          {isLoadingMore ? 'Loading...' : 'Load more'}
        </button>
      )}
    </div>
  );
}
